// ADR 5.9.26p §1 — Google Identity Services, the button and nothing else.
//
// No One Tap and no FedCM this release: both need `identity-credentials-get` in
// the app host's `Permissions-Policy`, which 5.9.26k does not grant. The
// rendered button alone needs only COOP `same-origin-allow-popups`, which it
// does. So `prompt()` is never called here, and its absence is the decision.
//
// `client_id` is `VITE_GOOGLE_CLIENT_ID` and must equal the value at
// `HSKVocabBackend/config/app.toml:74` — the same `aud` the backend pins with
// `set_audience` (`services/social_auth.rs:135,167`). A mismatch is a 401 the
// browser cannot explain, so the two are one value in two files by necessity.

import { afterFirstPaint } from '../app/firstPaint'

/** The GIS callback payload; `credential` is the Google ID token. */
export interface GoogleCredentialResponse {
  credential: string
}

export interface GoogleIdentityApi {
  initialize(config: {
    client_id: string
    callback: (response: GoogleCredentialResponse) => void
    /** ADR 24.9.26v — echoed into the ID token's `nonce` claim, raw. */
    nonce?: string
  }): void
  renderButton(parent: HTMLElement, options: Record<string, unknown>): void
}

interface GoogleGlobal {
  google?: { accounts?: { id?: GoogleIdentityApi } }
}

export const GOOGLE_CLIENT_ID: string = import.meta.env.VITE_GOOGLE_CLIENT_ID ?? ''

/**
 * The GIS API, or `null` when the script `whenIdentityReady()` inserts has not
 * loaded — a blocked third-party script, an offline first paint, or a test.
 */
export function identityApi(): GoogleIdentityApi | null {
  return (window as unknown as GoogleGlobal).google?.accounts?.id ?? null
}

/** The `id` the GIS script tag carries (ADR 15.9.26c §1; inserted since 24.9.26af). */
export const GIS_SCRIPT_ID = 'gsi-client'

/** 24.9.26af §1 — the GIS script URL; exactly the one `script-src` names. */
export const GIS_SRC = 'https://accounts.google.com/gsi/client'

/**
 * How long `whenIdentityReady()` waits for the script before giving up.
 *
 * Longer than any cold fetch of the ~200 KB script on a connection that can
 * run the app at all, and short enough that a visitor whose network blocks
 * Google is not left waiting on nothing: the email form is rendered beneath
 * the button from the first frame either way, so this bounds only how long
 * an empty host `div` sits in the DOM before `hidden` is set. The next change
 * to this number is meant to be evidence-led — `social_button_unavailable`
 * (§4) records which reason dominates.
 */
export const GIS_READY_TIMEOUT_MS = 5_000

/**
 * Why the GIS API never became available (ADR 15.9.26c §2, 24.9.26af §2).
 *
 * Since 24.9.26af the listeners attach before the script enters the document,
 * so `error` is a blocked or failed script, *observed* — an extension, DNS,
 * TLS, a CSP refusal. `timeout` is what is left: a slow or hanging fetch, or a
 * `load` that defined no global. `no_script` is retired: readiness inserts the
 * script rather than finding none.
 */
export type IdentityUnavailableReason = 'timeout' | 'error'

export type IdentityReadiness =
  | { api: GoogleIdentityApi }
  | { api: null; reason: IdentityUnavailableReason }

/**
 * The one attempt this page has made, keyed on the element it watches (24.9.26af §1).
 * ADR 1.10.26p §3 — `script` is `null` while the insertion waits for the first
 * paint: the attempt is memoised at request time, so a second call inside that
 * window (StrictMode's double mount) shares it rather than scheduling another.
 */
let attempt: { script: HTMLElement | null; ready: Promise<IdentityReadiness> } | null = null

/**
 * Resolve the GIS API once the script has executed, or `{ api: null, reason }`
 * when it has not within `timeoutMs` (blocked, failed, or slow).
 *
 * ADR 15.9.26c §2 — on a cold cache the app's bootstrap finishes before the
 * script executes, so a one-shot probe of `window.google` at mount loses the
 * race. This waits for the platform's own `load` event instead of polling, and
 * bounds the wait so a blocked script still degrades to the email form.
 *
 * ADR 24.9.26af §1 — the order is the decision: the global first (the warm
 * path, and evidence that overturns a settled failure), then a memoised
 * attempt whose element is still in the document, then a new attempt that
 * inserts the script. So GIS loads only where a sign-in panel mounts, once
 * per page — and, since ADR 1.10.26p §3, after the first paint, which this
 * comment claimed before it was true: the insertion was synchronous, and the
 * 102 KB script started before the observed LCP.
 */
export function whenIdentityReady(timeoutMs = GIS_READY_TIMEOUT_MS): Promise<IdentityReadiness> {
  // Resolved synchronously, with no `await` ahead of it: one microtask, not
  // two, and no timer. The warm-cache path must not regress, and the existing
  // `/auth/social` test reads the stub after exactly one microtask turn.
  const api = identityApi()
  if (api !== null) return Promise.resolve({ api })

  if (attempt !== null && (attempt.script === null || attempt.script.isConnected)) return attempt.ready
  return loadIdentityScript(timeoutMs)
}

/**
 * 24.9.26af §1 — insert #gsi-client if absent; one attempt per element, memoised.
 *
 * Mirrors `insertScript` in `features/ads/adsense.ts` (memoised, listeners
 * before append) without sharing it: AdSense rejects on `error`, readiness
 * resolves a counted reason with a timeout. An existing element is adopted —
 * that path exists for tests, which stage `#gsi-client` by hand, and is not
 * support for a static tag. Removing the element resets the memo, which is
 * how the suites that stage GIS start each case clean.
 *
 * ADR 1.10.26p §3 — adoption stays synchronous (nothing is downloaded on that
 * path, and 15.9.26c's timer arithmetic is unchanged); a **new** element is
 * inserted from `afterFirstPaint`, and its readiness timer starts there, so
 * the deferral can never turn into a `timeout`.
 */
function loadIdentityScript(timeoutMs: number): Promise<IdentityReadiness> {
  const existing = document.getElementById(GIS_SCRIPT_ID)
  if (existing !== null) {
    const adopted = { script: existing, ready: watchIdentityScript(existing, timeoutMs) }
    attempt = adopted
    return adopted.ready
  }
  const pending: { script: HTMLElement | null; ready: Promise<IdentityReadiness> } = {
    script: null,
    ready: new Promise((resolve) => {
      afterFirstPaint(window, () => {
        pending.script = insertIdentityScript(timeoutMs, resolve)
      })
    }),
  }
  attempt = pending
  return pending.ready
}

/**
 * The deferred step: adopt a `#gsi-client` that appeared while the attempt was
 * pending, else create and append one. Listeners are attached (synchronously,
 * inside the executor) before the new element enters the document, so an
 * early `error` is observable.
 */
function insertIdentityScript(
  timeoutMs: number,
  resolve: (readiness: Promise<IdentityReadiness>) => void,
): HTMLElement {
  const staged = document.getElementById(GIS_SCRIPT_ID)
  const script = staged ?? createIdentityScript()
  resolve(watchIdentityScript(script, timeoutMs))
  if (staged === null) document.head.append(script)
  return script
}

function createIdentityScript(): HTMLScriptElement {
  const script = document.createElement('script')
  script.id = GIS_SCRIPT_ID
  // No `defer`: it has no effect on an inserted script.
  script.async = true
  script.src = GIS_SRC
  return script
}

function watchIdentityScript(script: HTMLElement, timeoutMs: number): Promise<IdentityReadiness> {
  return new Promise((resolve) => {
    // On the adopt path a `load` (or `error`) that fired before these
    // listeners attached is unobservable — `HTMLScriptElement` has no
    // `readyState` — so the timer is the only bound there, and it reports
    // `timeout`, which is all that was observed.
    const settle = (readiness: IdentityReadiness) => {
      script.removeEventListener('load', onLoad)
      script.removeEventListener('error', onError)
      clearTimeout(timer)
      resolve(readiness)
    }
    const onLoad = () => {
      // A script that executed without defining the global (a Google-side
      // shape change) falls through to the timeout rather than resolving early,
      // so a `reason` never claims more than was observed.
      const loaded = identityApi()
      if (loaded !== null) settle({ api: loaded })
    }
    const onError = () => settle({ api: null, reason: 'error' })
    const timer = setTimeout(() => settle({ api: null, reason: 'timeout' }), timeoutMs)
    script.addEventListener('load', onLoad)
    script.addEventListener('error', onError)
  })
}

/**
 * Render the Google button into `parent`, calling `onCredential` with the
 * `id_token` to post to `/auth/social`.
 *
 * `nonce` is the server-minted one (ADR 24.9.26v §4). GIS takes it at
 * `initialize`, before any click, which is why the caller prefetches and
 * re-initialises after every settled attempt; `null` (the fetch failed)
 * initialises without one and the server decides.
 *
 * Returns `false` when there is nothing to render, so the caller can leave the
 * email form as the only door rather than showing an empty box.
 */
export function renderGoogleButton(
  parent: HTMLElement,
  onCredential: (idToken: string) => void,
  nonce: string | null,
): boolean {
  const api = identityApi()
  if (api === null || GOOGLE_CLIENT_ID === '') return false

  api.initialize({
    client_id: GOOGLE_CLIENT_ID,
    callback: (response) => onCredential(response.credential),
    ...(nonce === null ? {} : { nonce }),
  })
  api.renderButton(parent, { type: 'standard', theme: 'outline', size: 'large' })
  return true
}
